import * as THREE from 'three';

const tmp = new THREE.Vector3();

/**
 * World 3D -> toạ độ pixel trong overlay.
 * `visible` = false khi điểm nằm sau camera; `depth` = khoảng cách theo trục nhìn.
 */
export const projectToScreen = (point, camera, width, height) => {
  tmp.copy(point).applyMatrix4(camera.matrixWorldInverse);
  const depth = -tmp.z;
  const inFront = tmp.z < -(camera.near ?? 0);
  tmp.copy(point).project(camera);
  return {
    x: (tmp.x * 0.5 + 0.5) * width,
    y: (-tmp.y * 0.5 + 0.5) * height,
    visible: inFront,
    // Khoảng cách theo hướng nhìn (dùng để nội suy đúng phối cảnh)
    depth,
  };
};

/**
 * Tính hình học 2D của một DIM đã resolve (xem DimensionEngine.resolveDimension).
 */
export const projectDimension = (resolved, camera, width, height) => {
  const s1 = projectToScreen(resolved.p1, camera, width, height);
  const s2 = projectToScreen(resolved.p2, camera, width, height);
  const t1 = projectToScreen(resolved.q1, camera, width, height);
  const t2 = projectToScreen(resolved.q2, camera, width, height);
  const dx = t2.x - t1.x;
  const dy = t2.y - t1.y;
  const lengthPx = Math.hypot(dx, dy);
  return {
    s1,
    s2,
    t1,
    t2,
    mid: { x: (t1.x + t2.x) / 2, y: (t1.y + t2.y) / 2 },
    angle: Math.atan2(dy, dx),
    lengthPx,
    visible: s1.visible && s2.visible && t1.visible && t2.visible,
  };
};

/**
 * Vector màn hình (px) tương ứng với việc đẩy đường DIM ra thêm 1 mét theo
 * hướng offset. Dùng để đổi thao tác kéo chuột (px) thành offsetM (mét).
 * Đo bằng đoạn thử ngắn để tránh méo phối cảnh khi điểm gần camera.
 * @returns {{x:number,y:number}|null} null nếu không xác định được hướng
 */
export const offsetPixelsPerMeter = (resolved, camera, width, height) => {
  const PROBE_M = 0.1;
  const a = projectToScreen(resolved.q1, camera, width, height);
  const b = projectToScreen(
    resolved.q1.clone().addScaledVector(resolved.offsetDir, PROBE_M),
    camera,
    width,
    height
  );
  if (!a.visible || !b.visible) return null;
  const v = { x: (b.x - a.x) / PROBE_M, y: (b.y - a.y) / PROBE_M };
  return Math.hypot(v.x, v.y) < 1e-6 ? null : v;
};

/** offsetM mới sau khi kéo chuột `delta` (px) từ offset ban đầu. */
export const dragOffsetM = (startOffsetM, delta, pxPerMeter, limit = 10) => {
  if (!pxPerMeter) return startOffsetM;
  const lenSq = pxPerMeter.x ** 2 + pxPerMeter.y ** 2;
  const change = (delta.x * pxPerMeter.x + delta.y * pxPerMeter.y) / lenSq;
  return Math.max(-limit, Math.min(limit, startOffsetM + change));
};
